import { useFrame } from '@react-three/fiber';
import { useMemo, useState } from 'react';
import { Object3D } from 'three';

import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';

import useLoadAnimation, { AnimationData } from 'hooks/useLoadAnimation';
import { projectStore } from 'stores';

import ActiveCharacter from './ActiveCharacter';

export default function DefaultAnimations() {
  const defaultAnimationPaths = useMemo(() => getDefaultAnimation(), []);

  const animations = useLoadAnimation(defaultAnimationPaths);

  if (!animations) return null;

  return <Stage animations={animations} />;
}

function Stage({ animations }: { animations: Array<AnimationData> }) {
  const [animData, setAnimData] = useState<AnimationData>(animations[0]);
  const [characterScene, setCharacterScene] = useState<Object3D | null>(null);

  // Swop to a new clip when the last one ends
  const onLoopFinish = () => {
    if (animations.length > 0) {
      const idx = (animations.indexOf(animData) + 1) % animations.length;
      setAnimData(animations[idx]);
    }
  };

  const mixer = useFrameMixer(
    animData.animationData.clip,
    characterScene ?? undefined,
    {
      frameRate: 60,
      autoplay: true,
      onLoopFinish,
    },
  );

  useFrame((state, delta) => {
    mixer?.update(delta);
  });

  const handleCharacterLoading = (loading: boolean) => {
    projectStore.setMeshLoadingStatus(loading ? 'pending' : 'ready');
  };

  return (
    <ActiveCharacter
      onSceneReady={setCharacterScene}
      onCharacterLoading={handleCharacterLoading}
    />
  );
}

const DEFAULT_ANIMATION_COUNT = 3;
const getDefaultAnimation = (): Array<string> => {
  // Create an array filled with 1 to N
  const out: Array<string> = Array.from(
    { length: DEFAULT_ANIMATION_COUNT },
    (_, i) => (i + 1).toString(),
  );

  // Randomly swop elements, better results then sort( random () )
  out.forEach((_, i, a) => {
    const ri = Math.floor(Math.random() * DEFAULT_ANIMATION_COUNT);
    [a[i], a[ri]] = [a[ri], a[i]];
  });

  // Fill Array with file paths
  out.forEach((e, i, a) => {
    a[i] = `/default-animations${e}.bvh`;
  });

  return out;
};
